import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Layout from '../components/Layout';
import Avatar from '../components/Avatar';
import { assessmentsService } from '../api/assessmentsService';
import { useAuth } from '../contexts/AuthContext';
import { getImageUrl } from '../utils/imageUrl';

type RatingValue = 'red' | 'yellow' | 'green';

const ratingDotClass: Record<RatingValue, string> = {
  red: 'bg-red-500',
  yellow: 'bg-yellow-400',
  green: 'bg-green-500',
};

const ratingLabel: Record<RatingValue, string> = {
  red: 'Red',
  yellow: 'Yellow',
  green: 'Green',
};

const displayCandidateName = (c: any) =>
  c?.name || [c?.firstName, c?.lastName].filter(Boolean).join(' ') || '—';

const displaySpouseName = (c: any) =>
  c?.spouseName || [c?.wifeFirstName, c?.wifeLastName].filter(Boolean).join(' ') || '';

const AssessmentAfterParty: React.FC = () => {
  const { id } = useParams();
  const assessmentId = useMemo(() => parseInt(id || '0', 10), [id]);
  const { user } = useAuth();
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  const hasRole = (roleName: string) => (
    user?.roles?.some((role) => (typeof role === 'string' ? role === roleName : role.name === roleName)) || false
  );
  const canSeeAfterParty = hasRole('assessor') || hasRole('admin') || hasRole('staff');

  const reportAssignmentsQuery = useQuery({
    queryKey: ['assessments', assessmentId, 'report-assignments'],
    queryFn: () => assessmentsService.listReportAssignments(assessmentId),
    enabled: canSeeAfterParty && Number.isFinite(assessmentId) && assessmentId > 0,
  });
  const assessmentQuery = useQuery({
    queryKey: ['assessments', assessmentId],
    queryFn: () => assessmentsService.get(assessmentId),
    enabled: canSeeAfterParty && Number.isFinite(assessmentId) && assessmentId > 0,
  });

  const reportGroups = useMemo(() => {
    const assignments = reportAssignmentsQuery.data?.data || [];
    const grouped = new Map<number, { participant: any; assignments: any[] }>();
    for (const assignment of assignments) {
      const participant = assignment.participant;
      if (!participant) continue;
      const current = grouped.get(participant.id) || { participant, assignments: [] };
      current.assignments.push(assignment);
      grouped.set(participant.id, current);
    }
    return Array.from(grouped.values()).map((group) => ({
      ...group,
      assignments: group.assignments
        .slice()
        .sort((a, b) => ((a.role?.sortOrder ?? 0) - (b.role?.sortOrder ?? 0)) || ((a.role?.displayName || '').localeCompare(b.role?.displayName || ''))),
    }));
  }, [reportAssignmentsQuery.data?.data]);

  const toggleExpanded = (participantId: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(participantId)) {
        next.delete(participantId);
      } else {
        next.add(participantId);
      }
      return next;
    });
  };

  if (!canSeeAfterParty) {
    return (
      <Layout>
        <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6 text-gray-700">
          You do not have access to this page.
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">After Party</h1>
              <p className="text-gray-600 mt-1">All assessment reports by candidate and area.</p>
            </div>
            <Link to={`/assessments/${assessmentId}`} className="text-sm text-primary-600 hover:text-primary-700">
              Back to Assessment
            </Link>
          </div>
        </div>

        {reportAssignmentsQuery.isLoading && (
          <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6 text-gray-700">Loading reports…</div>
        )}

        {reportAssignmentsQuery.isError && (
          <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6 text-red-700">Failed to load reports.</div>
        )}

        {!reportAssignmentsQuery.isLoading && !reportAssignmentsQuery.isError && reportGroups.length === 0 && (
          <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6 text-gray-600">No report assignments available yet.</div>
        )}

        {!reportAssignmentsQuery.isLoading && !reportAssignmentsQuery.isError && reportGroups.length > 0 && (
          <div className="space-y-4">
            {reportGroups.map((group) => {
              const portalCandidate = group.participant?.portalCandidate;
              const candidateName = displayCandidateName(portalCandidate);
              const spouseName = displaySpouseName(portalCandidate);
              const candidateLabel = spouseName ? `${candidateName} & ${spouseName}` : candidateName;
              const targetLocation = (portalCandidate?.targetLocationMississippi || '').trim();
              const familyPhotoUrl = getImageUrl(portalCandidate?.familyPhotoImagePath, { width: 640, quality: 70, format: 'webp' });
              const headshotUrl = getImageUrl(portalCandidate?.headshotImagePath, { width: 160, quality: 70, format: 'webp' });
              const isExpanded = expandedIds.has(group.participant.id);
              const submittedCount = group.assignments.filter((assignment) => (
                ['submitted', 'reviewed', 'approved'].includes(assignment.latestReport?.status || '')
              )).length;

              return (
                <div key={group.participant.id} className="bg-white shadow-sm rounded-xl border border-gray-200 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => toggleExpanded(group.participant.id)}
                    className="w-full text-left hover:bg-gray-50"
                  >
                    <div className="relative h-28 sm:h-32 w-full">
                      {familyPhotoUrl ? (
                        <img src={familyPhotoUrl} alt={`${candidateLabel} family`} className="h-full w-full object-cover" />
                      ) : (
                        <div className="h-full w-full bg-gradient-to-r from-gray-100 to-gray-200" />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
                      <div className="absolute bottom-3 left-4 sm:left-6 flex items-center gap-3">
                        <Avatar name={candidateName} size="md" imageUrl={headshotUrl} />
                        <div>
                          <div className="flex items-center gap-2 text-sm font-semibold text-white">
                            {candidateLabel}
                            {assessmentQuery.data?.data?.isDoubleAssessment && (
                              <span className="rounded-full bg-white/20 px-2 py-0.5 text-[10px] uppercase tracking-wide">Team {group.participant.teamKey || 'A'}</span>
                            )}
                          </div>
                          <div className="text-xs text-white/80">
                            {targetLocation || 'Target location not provided'}
                          </div>
                        </div>
                      </div>
                      <div className="absolute bottom-3 right-4 sm:right-6 flex items-center gap-3 text-xs text-white/80">
                        <span>{submittedCount} of {group.assignments.length} submitted</span>
                        <Link
                          to={`/assessments/${assessmentId}/final-reports/${group.participant.id}`}
                          className="rounded-full bg-black/40 px-3 py-1 text-white hover:bg-black/60"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Final report
                        </Link>
                        <span>{isExpanded ? 'Hide reports' : 'View reports'}</span>
                      </div>
                    </div>

                    <div className="px-4 py-4 sm:px-6 flex flex-wrap items-center gap-3">
                      {group.assignments.map((assignment) => {
                        const roleLabel = assignment.role?.displayName || assignment.role?.name || 'Area';
                        const rating = assignment.latestReport?.initialOverallRating as RatingValue | undefined;
                        const scorecard = assignment.scorecardOverview;
                        return (
                          <div key={`${assignment.participantId}-${assignment.roleId}`} className="flex items-center gap-2 text-xs text-gray-600">
                            <span className="font-medium">{roleLabel}</span>
                            {rating ? (
                              <span className="flex items-center gap-1">
                                <span className={['inline-flex h-2.5 w-2.5 rounded-full', ratingDotClass[rating]].join(' ')} />
                                <span className="text-gray-500">{ratingLabel[rating]}</span>
                              </span>
                            ) : (
                              <span className="text-gray-400">No rating</span>
                            )}
                            {scorecard && scorecard.ratedCount > 0 && scorecard.average !== null && (
                              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600">
                                Scorecard {scorecard.average.toFixed(1)}/4
                                <span className="text-gray-400"> ({scorecard.ratedCount}/{scorecard.totalCount})</span>
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="px-4 pb-4 sm:px-6 sm:pb-6 space-y-4">
                      <div className="space-y-3">
                        {group.assignments.map((assignment) => {
                          const roleLabel = assignment.role?.displayName || assignment.role?.name || 'Area';
                          const assessorName = assignment.assessor
                            ? `${assignment.assessor.firstName || ''} ${assignment.assessor.lastName || ''}`.trim()
                            : '';
                          const report = assignment.latestReport;
                          const reportNotes = report?.content?.trim() || '';
                          const afterPartNotes = report?.afterPartNotes?.trim() || '';

                          return (
                            <div key={`${assignment.participantId}-${assignment.roleId}`} className="rounded-lg border border-gray-200 p-4">
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <div className="text-sm font-semibold text-gray-900">
                                  {roleLabel}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {assessorName ? `Assessor: ${assessorName}` : 'Assessor: —'}
                                </div>
                              </div>
                              {report ? (
                                <div className="mt-3 space-y-3">
                                  <div>
                                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Report Notes</div>
                                    <div className="text-sm text-gray-800 whitespace-pre-wrap">{reportNotes || '—'}</div>
                                  </div>
                                  <div>
                                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">After-Part Notes</div>
                                    <div className="text-sm text-gray-800 whitespace-pre-wrap">{afterPartNotes || '—'}</div>
                                  </div>
                                </div>
                              ) : (
                                <div className="mt-3 text-sm text-gray-500">No report submitted yet.</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AssessmentAfterParty;
